import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { logRelayMigrationDiagnostic } from './relayDiagnostics.ts';
import { getRelayOperationKey, type RelayOperationVariables } from './relayOperation.ts';

const CONFLUENCE_PACKAGE_PREFIX = '@confluence/';

let disableRelayOverride: boolean | undefined;

export function __TEST_ONLY_resetDisableRelayOverride(): void {
	disableRelayOverride = undefined;
}

type RelayDecisionPhase =
	| 'preload'
	| 'component'
	| 'lazy'
	| 'imperative'
	| 'cache'
	| 'mutation'
	| 'fragment'
	| 'subscription'
	| 'memo'
	| 'unknown';

export type RelayEnablementOptions = {
	operationName?: string | null;
	operationKey?: string | null;
	variables?: RelayOperationVariables;
	phase?: RelayDecisionPhase;
};

function isRelayDisabledByQueryParam(): boolean {
	if (disableRelayOverride !== undefined) {
		return disableRelayOverride;
	}

	try {
		if (typeof window !== 'undefined' && window.location?.search) {
			const params = new URLSearchParams(window.location.search);
			const value = params.get('disableRelay');
			disableRelayOverride = value === '1' || value === 'true';
		} else {
			disableRelayOverride = false;
		}
	} catch {
		disableRelayOverride = false;
	}

	return disableRelayOverride;
}

function logRelayDecision({
	callerPackage,
	operationName,
	operationKey,
	phase,
	relayEnabled,
	decisionReason,
}: {
	callerPackage?: string;
	operationName?: string | null;
	operationKey?: string | null;
	phase: RelayDecisionPhase;
	relayEnabled: boolean;
	decisionReason: string;
}): void {
	if (!fg('confluence_relay_migration_diagnostics_enabled')) {
		return;
	}

	logRelayMigrationDiagnostic('Decision', {
		phase,
		callerPackage,
		operationName,
		operationKey,
		relayEnabled,
		decisionReason,
	});
}

export function isRelayEnabled(
	callerPackage?: string,
	options: RelayEnablementOptions = {},
): boolean {
	const phase = options.phase ?? 'unknown';
	const operationName = options.operationName ?? null;
	const operationKey =
		options.operationKey ?? getRelayOperationKey(operationName, options.variables) ?? null;

	// Query param override: ?disableRelay=1 or ?disableRelay=true forces Relay off
	if (isRelayDisabledByQueryParam()) {
		logRelayDecision({
			callerPackage,
			operationName,
			operationKey,
			phase,
			relayEnabled: false,
			decisionReason: 'disableRelay query param',
		});
		return false;
	}

	if (process.env.BUILD_VARIANT === 'relay') {
		logRelayDecision({
			callerPackage,
			operationName,
			operationKey,
			phase,
			relayEnabled: true,
			decisionReason: 'BUILD_VARIANT relay',
		});
		return true;
	}

	if (!expVal('confluence_apollo_to_relay_migration', 'isEnabled', false)) {
		logRelayDecision({
			callerPackage,
			operationName,
			operationKey,
			phase,
			relayEnabled: false,
			decisionReason: 'master experiment disabled',
		});
		return false;
	}

	if (operationKey) {
		const operations = StatsigConfigurations.getStringList(
			'confluence_relay_enabled_operations',
			[],
		);
		const relayEnabled = operations.includes(operationKey);
		logRelayDecision({
			callerPackage,
			operationName,
			operationKey,
			phase,
			relayEnabled,
			decisionReason: 'operation allowlist',
		});
		return relayEnabled;
	}

	if (!callerPackage) {
		logRelayDecision({
			callerPackage,
			operationName,
			operationKey,
			phase,
			relayEnabled: false,
			decisionReason: 'missing operation and caller package',
		});
		return false;
	}

	if (!fg('confluence_relay_package_rollout_enabled')) {
		logRelayDecision({
			callerPackage,
			operationName,
			operationKey,
			phase,
			relayEnabled: false,
			decisionReason: 'package rollout feature gate disabled',
		});
		return false;
	}

	const packages = StatsigConfigurations.getStringList('confluence_relay_enabled_packages', []);
	const packageName = callerPackage.startsWith(CONFLUENCE_PACKAGE_PREFIX)
		? callerPackage.slice(CONFLUENCE_PACKAGE_PREFIX.length)
		: callerPackage;
	const relayEnabled =
		packages.includes(packageName) ||
		packages.includes(`${CONFLUENCE_PACKAGE_PREFIX}${packageName}`);
	logRelayDecision({
		callerPackage,
		operationName,
		operationKey,
		phase,
		relayEnabled,
		decisionReason: 'package allowlist',
	});
	return relayEnabled;
}
